import React, {
    useEffect,
    useState,
    useRef,
    ForwardRefRenderFunction,
    useImperativeHandle,
    forwardRef,
    ForwardedRef,
} from 'react';
import 'react-checkbox-tree/lib/react-checkbox-tree.css';
import { FormattedMessage, WrappedComponentProps, useIntl } from 'react-intl';
import { Buckets } from '../../../models/buckets-enum';
import { ICompanyAccount } from '../../../models/company-account';
import { ISession } from '../../../models/pro-sites';
import { IBucket } from '../../../models/prostatic';
import { IRole } from '../../../models/role';
import { IUserModel } from '../../../models/user-model';
import BucketFlagged from '../../authorization/bucket-flagged';
import CheckboxList from '../../inputs/checkbox-list';
import EmailInput from '../../inputs/email-input';
import '../../inputs/react-checkbox-tree.css';
import SelectInput from '../../inputs/select-input';
import TextInput from '../../inputs/text-input';
import UserAccountsTree from './user-accounts-tree';
import UserNotificationPreferences from '../user-notification-preferences';
import { IValidationHandles } from '../../../models/validation-handles';
import { IUserNotificationPreferences } from '../../../models/user-notification-preferences';
import { FieldValidation, validate } from '../../../validation/validation';
import * as Validators from '../../../validation/validators';
import parse from 'html-react-parser';

const handleNotificationChanged = (
    p: IUserNotificationPreferences,
    onChange: (e: any) => void
): void => {
    const e = { target: { name: 'notificationPreferences', value: p } };
    onChange(e);
};

export class UserFormErrors {
    firstName? = '';
    lastName? = '';
    directoryUser? = '';
    confirmDirectoryUser? = '';
    onSave? = '';
}

interface IEditUserFormProps {
    session: ISession;
    user: IUserModel;
    editMode: boolean;
    companyAccounts: ICompanyAccount[];
    companyBuckets: IBucket[];
    companyRoles: IRole[];
    saving: boolean;
    errors: UserFormErrors;
    onChange: (event: any) => void;
    onDelete: () => void;
    onMove: () => void;
    onSave: (event: React.ChangeEvent<HTMLFormElement>) => void;
    onCancel: () => void;
    onErrorsChanged: (e: UserFormErrors) => void;
}

export const EditUserForm = forwardRef(
    (props: IEditUserFormProps, ref: ForwardedRef<IValidationHandles>) => {
        const [buckets, setBuckets] = useState<string[]>([]);
        const intl = useIntl();

        const notificationPreferencesRef = useRef<IValidationHandles>(null);

        const {
            session,
            user,
            editMode,
            companyAccounts,
            companyBuckets,
            companyRoles,
            saving = false,
            errors = new UserFormErrors(),
            onChange,
            onDelete,
            onMove,
            onSave,
            onCancel,
            onErrorsChanged,
        } = props;

        useEffect(() => {
            if (user.buckets.length > 0 && buckets.length === 0) {
                setBuckets(user.buckets);
            }
        }, [buckets.length, user.buckets, user.buckets.length]);

        const handleBucketsChanged = (event: any) => {
            onChange(event);
            onChange({ target: { name: 'role', value: 9 } });
        };

        const handleRoleChanged = (event: any) => {
            onChange(event);

            const role = companyRoles.find(
                (x) => x.id.toString() === event.target.value
            );
            const buckets = role ? role!.buckets : [];

            if (buckets.length > 0) {
                // reset the checkboxes, otherwise leave what is currently selected.
                onChange({ target: { name: 'buckets', value: buckets } });
            }
        };

        const fieldValidations = [
            new FieldValidation('firstName', 'First Name', [
                Validators.required(
                    intl.formatMessage(
                        {
                            id: 'Required_Field_Message',
                            defaultMessage: '{0} is required',
                        },
                        {
                            0: intl.formatMessage({ id: 'First_Name' }),
                        }
                    )
                ),
            ]),
            new FieldValidation('lastName', 'Last Name', [
                Validators.required(
                    intl.formatMessage(
                        {
                            id: 'Required_Field_Message',
                            defaultMessage: '{0} is required',
                        },
                        {
                            0: intl.formatMessage({ id: 'Last_Name' }),
                        }
                    )
                ),
            ]),
            new FieldValidation('directoryUser', 'Email Address', [
                Validators.required(
                    intl.formatMessage(
                        {
                            id: 'Required_Field_Message',
                            defaultMessage: '{0} is required',
                        },
                        {
                            0: intl.formatMessage({ id: 'Email_Address' }),
                        }
                    )
                ),
                Validators.isEmail(
                    intl.formatMessage({
                        id: 'Invalid_Email_Address',
                        defaultMessage: '',
                    })
                ),
            ]),
            new FieldValidation('confirmDirectoryUser', 'Confirm Email', [
                Validators.mustMatch(
                    'directoryUser',
                    'Email Address',
                    intl.formatMessage(
                        { id: 'Fields_Must_Match' },
                        {
                            0: intl.formatMessage({ id: 'Confirm_Email' }),
                            1: intl.formatMessage({ id: 'Email_Address' }),
                        }
                    )
                ),
            ]),
        ];

        const isValid = (): boolean => {
            const validations = editMode
                ? fieldValidations.filter(
                      (x) =>
                          x.name !== 'directoryUser' &&
                          x.name !== 'confirmDirectoryUser'
                  )
                : fieldValidations;

            const formErrors: UserFormErrors = validate(user, validations);
            const preferencesIsValid = notificationPreferencesRef.current
                ? notificationPreferencesRef.current!.isValid()
                : true;

            onErrorsChanged(formErrors);

            return Object.keys(formErrors).length === 0 && preferencesIsValid;
        };

        useImperativeHandle(ref, () => ({
            isValid: () => {
                return isValid();
            },
        }));

        const formInvalid = () => {
            const errorLength = Object.keys(errors).length;
            return errorLength > 0 && !(errorLength === 1 && errors.onSave);
        };

        return (
            <form onSubmit={onSave}>
                <div className="well">
                    <div className="row">
                        <div className="col-md-6">
                            <h3 className="underline">
                                <FormattedMessage
                                    id="User_Information"
                                    defaultMessage="User Information"
                                />
                            </h3>
                            <div className="form-horizontal" role="form">
                                <TextInput
                                    name="firstName"
                                    label={'First Name'}
                                    value={user.firstName}
                                    maxLength={30}
                                    onChange={onChange}
                                    required={true}
                                    showError={true}
                                    error={errors.firstName}
                                />
                                <TextInput
                                    name="lastName"
                                    label={'Last Name'}
                                    value={user.lastName}
                                    onChange={onChange}
                                    maxLength={30}
                                    required={true}
                                    showError={true}
                                    error={errors.lastName}
                                />
                                {editMode && (
                                    <div className="form-group">
                                        <label className="control-label col-sm-4">
                                            <FormattedMessage
                                                id="Email_Address"
                                                defaultMessage="Email Address"
                                            />
                                        </label>
                                        <div className="col-sm-8">
                                            <p className="form-control-static">
                                                {user.directoryUser}
                                            </p>
                                        </div>
                                    </div>
                                )}
                                {!editMode && (
                                    <>
                                        <EmailInput
                                            name="directoryUser"
                                            label={'Email Address'}
                                            value={user.directoryUser}
                                            required={true}
                                            showError={true}
                                            error={errors.directoryUser}
                                            onChange={onChange}
                                        />
                                        <EmailInput
                                            name="confirmDirectoryUser"
                                            label={'Confirm Email'}
                                            value={user.confirmDirectoryUser}
                                            required={true}
                                            showError={true}
                                            error={errors.confirmDirectoryUser}
                                            onChange={onChange}
                                        />
                                    </>
                                )}
                                <div className="row">
                                    <div className="col-md-12">
                                        <UserNotificationPreferences
                                            formClass=" "
                                            preferences={
                                                user.notificationPreferences
                                            }
                                            onValueChange={(p) =>
                                                handleNotificationChanged(
                                                    p,
                                                    onChange
                                                )
                                            }
                                            ref={notificationPreferencesRef}
                                        />
                                    </div>
                                </div>
                                <h3 className="underline">
                                    <FormattedMessage
                                        id="Security_Access"
                                        defaultMessage="Security Access"
                                    />
                                </h3>
                                <SelectInput
                                    name="role"
                                    label={'Role'}
                                    value={
                                        user.role ? user.role.toString() : '2'
                                    }
                                    options={companyRoles.map((r) => ({
                                        value: r.id,
                                        text: r.name,
                                    }))}
                                    onChange={handleRoleChanged}
                                />
                                <div className="form-group">
                                    <label className="control-label col-sm-4">
                                        <FormattedMessage
                                            id="Job_Functions"
                                            defaultMessage="Job Functions"
                                        />
                                        :
                                    </label>
                                    <div className="col-sm-8">
                                        <CheckboxList
                                            name="buckets"
                                            checkedItems={user.buckets}
                                            items={companyBuckets.map(
                                                (item) => ({
                                                    label: intl.formatMessage({
                                                        id: item.name
                                                            .replace(' ', '_')
                                                            .replace(
                                                                '&',
                                                                'AND'
                                                            ),
                                                        defaultMessage:
                                                            item.name,
                                                    }),
                                                    name: item.name,
                                                    value: item.id.toString(),
                                                })
                                            )}
                                            onChange={handleBucketsChanged}
                                            disabled={session.buckets.includes(
                                                24
                                            )}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="form-horizontal col-md-6">
                            <h3 className="underline">
                                <FormattedMessage
                                    id="Location_Access"
                                    defaultMessage="Location Access"
                                />
                            </h3>
                            <div className="form-horizontal" role="form">
                                <div className="form-group">
                                    {companyAccounts.length > 0 ? (
                                        <label className="control-label col-sm-4 visible-sm-block">
                                            <FormattedMessage
                                                id="Locations"
                                                defaultMessage="Locations"
                                            />
                                            :
                                        </label>
                                    ) : (
                                        <p className="col-sm-4">
                                            <FormattedMessage id="No_Active_Locations" />
                                        </p>
                                    )}
                                    <div className="form-group col-xs-12 col-sm-8 col-md-12">
                                        {user && (
                                            <UserAccountsTree
                                                name="accounts"
                                                companyAccounts={
                                                    companyAccounts!
                                                }
                                                userAccounts={user!.accounts}
                                                onChange={onChange}
                                                session={session}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>{' '}
                {/* well */}
                {formInvalid() && (
                    <div className="alert alert-danger gutter-top">
                        <span>
                            <FormattedMessage
                                id="Please_enter_all_required_information"
                                defaultMessage="Please enter all required information before continuing"
                            />
                        </span>
                    </div>
                )}
                {errors.onSave && session.country === 'CA' && (
                    <div className="alert alert-danger gutter-top">
                        <span style={{ whiteSpace: 'pre-wrap' }}>
                            {intl.formatMessage({
                                id: 'User_role_error',
                                defaultMessage: errors.onSave,
                            })}
                        </span>
                    </div>
                )}
                {errors.onSave && session.country !== 'CA' && (
                    <div className="alert alert-danger gutter-top">
                        <span style={{ whiteSpace: 'pre-wrap' }}>
                            {parse(errors.onSave)}
                        </span>
                    </div>
                )}
                <div className="clearfix gutter-top">
                    <button
                        id="save-changes"
                        type="submit"
                        className="btn btn-primary"
                    >
                        <span>
                            {saving ? (
                                <>
                                    <FormattedMessage id="Saving" />
                                    {' ...'}
                                </>
                            ) : (
                                <FormattedMessage id="Save_User" />
                            )}
                        </span>
                    </button>
                    <button
                        className="btn btn-default"
                        type="button"
                        onClick={onCancel}
                    >
                        <FormattedMessage id="Cancel" />
                    </button>
                    {editMode && session.userName !== user.directoryUser && (
                        <div className="pull-right">
                            <button
                                className="btn btn-default hidden-xs"
                                style={{ marginLeft: '5px' }}
                                type="button"
                                onClick={onDelete}
                            >
                                <FormattedMessage id="Delete_User" />
                            </button>
                        </div>
                    )}
                    {editMode && (
                        <BucketFlagged
                            buckets={[Buckets.ManageCompanies]}
                            session={session}
                        >
                            <button
                                className="btn btn-default pull-right"
                                onClick={onMove}
                            >
                                <FormattedMessage
                                    id="Move_User"
                                    defaultMessage="Move User"
                                />
                            </button>
                        </BucketFlagged>
                    )}
                </div>
            </form>
        );
    }
);

export default EditUserForm;
